import { useMemo, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import {
  useListJobsV1JobsGet,
  useListRunsV1RunsGet,
} from "../api/generated/qcsControlPlane";
import { Flow, FlowCategory } from "../api/generated/model";
import {
  type Job,
  type Run,
  type RunStatus,
  CATEGORY_LABEL,
  FLOW_CATEGORY,
  elapsedSeconds,
  flowLabel,
  fmtDate,
  fmtDuration,
  fmtRows,
  fmtTime,
  platformLabel,
  statusTone,
} from "../api/labels";
import { type AnyColumnDef, DataTable, createColumnHelper } from "../components/DataTable";
import { Badge, Dot, ErrorState, PageHeader, Panel, Row, Switch, Tag } from "@akaike/ui";
import { Seg } from "../components/foundation";

/** Qcom's pill tones onto the shared Badge's toned outlines. */
const TONE_VARIANT = {
  ok: "outline-success",
  warn: "outline-warning",
  crit: "outline-destructive",
  info: "outline-info",
  brand: "outline-primary",
  idle: "outline",
} as const;

/** Flow types onto the same outlines. Categorical hues, not status - the chip is
 *  read with its flow name, and no hue here means better or worse than another. */
const FLOW_VARIANT: Record<string, "outline-info" | "outline-primary" | "outline-violet"> = {
  cart_inventory: "outline-info",
  brand_sweep: "outline-primary",
  merchant_sweep: "outline-violet",
};


const STATUS_FILTERS: ("all" | RunStatus)[] = [
  "all",
  "running",
  "succeeded",
  "degraded",
  "failed",
  "cancelled",
  "timed_out",
];

const FLOWS: Flow[] = Object.values(Flow);

export function Runs() {
  const [jobId, setJobId] = useState("all");
  const [status, setStatus] = useState<(typeof STATUS_FILTERS)[number]>("all");
  const [flow, setFlow] = useState<"all" | Flow>("all");
  const [category, setCategory] = useState<"all" | FlowCategory>("all");
  // An availability build with nothing to do still records a run, every quarter hour
  // per platform; hidden by default so the scrapes stay readable.
  const [showNoop, setShowNoop] = useState(false);

  // Include ad-hoc jobs so their runs still resolve a name/platform/flow (they are
  // hidden from the Jobs list, but their runs show here). The "all jobs" filter dropdown
  // below still lists only the normal, reusable jobs.
  const jobsQuery = useListJobsV1JobsGet({ include_ad_hoc: true });

  const runsQuery = useListRunsV1RunsGet(
    {
      limit: 100,
      job_id: jobId !== "all" ? jobId : undefined,
      status: status !== "all" ? status : undefined,
      flow: flow !== "all" ? flow : undefined,
      category: category !== "all" ? category : undefined,
      include_noop: showNoop,
    },
    { query: { refetchInterval: 5000 } },
  );

  // memoised: the `?? []` fallback is a new array each render, which defeats the memos below
  const jobs = useMemo(() => (jobsQuery.data ?? []) as Job[], [jobsQuery.data]);
  const jobById = useMemo(() => new Map(jobs.map((j) => [j.id, j])), [jobs]);
  const selectableJobs = useMemo(() => jobs.filter((j) => !j.ad_hoc), [jobs]);
  // memoised for the same reason as `jobs`: a fresh array every render hands the table a
  // new `data` identity on every poll tick, whether or not the rows changed
  const runs = useMemo(() => (runsQuery.data ?? []) as Run[], [runsQuery.data]);
  const runningCount = runs.filter(
    (r) => r.status === "running" || r.status === "claimed" || r.status === "queued",
  ).length;

  const columns = useMemo<AnyColumnDef<Run>[]>(() => {
    const col = createColumnHelper<Run>();
    const isLive = (r: Run) =>
      r.status === "running" || r.status === "claimed" || r.status === "queued";
    const isCrit = (r: Run) => r.status === "failed" || r.status === "timed_out";
    return [
      col.accessor((r) => r.run_id ?? "not started", {
        id: "run_id",
        header: "Run ID",
        cell: ({ row }) => {
          const r = row.original;
          if (!r.run_id) {
            // No run_id means the worker never claimed this run, so there is no
            // real ID to show. Render a muted label rather than a truncated raw
            // UUID that would masquerade as a real run ID.
            return (
              <Link
                to={`/runs/${r.id}`}
                className="id"
                style={{
                  color: "var(--ink-3)",
                  fontStyle: "italic",
                  textDecoration: "none",
                }}
                title="This run was queued but a worker never started it, so it never got a run ID."
              >
                not started
              </Link>
            );
          }
          const color = isCrit(r) ? "var(--crit)" : isLive(r) ? "var(--ink-2)" : "var(--info)";
          return (
            <Link to={`/runs/${r.id}`} className="id" style={{ color, textDecoration: "none" }}>
              {r.run_id}
            </Link>
          );
        },
        meta: { width: 130 },
      }),
      col.accessor((r) => jobById.get(r.job_id)?.name ?? r.job_id.slice(0, 8), {
        id: "job",
        header: "Job",
        cell: ({ row }) => {
          const job = jobById.get(row.original.job_id);
          if (!job) return <span className="faint">{row.original.job_id.slice(0, 8)}</span>;
          return (
            <Row gap="xs" className="min-w-0">
              {job.ad_hoc && (
                <Badge
                  variant="outline-info"
                  className="flex-none"
                  style={{ fontSize: 9.5, letterSpacing: 0.3 }}
                  title="A one-off, scope-pinned launch from the brand page (not a scheduled job)"
                >
                  ad-hoc
                </Badge>
              )}
              <span className="truncate">{job.name}</span>
            </Row>
          );
        },
        meta: { cellClassName: "strong", minWidth: 150 },
      }),
      col.accessor((r) => jobById.get(r.job_id)?.platform ?? "", {
        id: "platform",
        header: "Platform",
        cell: ({ row }) => {
          const job = jobById.get(row.original.job_id);
          return job ? (
            <span className={`plat ${job.platform}`}>{platformLabel(job.platform)}</span>
          ) : (
            <span className="faint">-</span>
          );
        },
        meta: { width: 110, filterSelect: true, filterLabel: "platform" },
      }),
      col.accessor((r) => jobById.get(r.job_id)?.flow ?? "", {
        id: "flow",
        header: "Flow",
        cell: ({ row }) => {
          const job = jobById.get(row.original.job_id);
          return job ? (
            <Badge variant={FLOW_VARIANT[job.flow] ?? "outline"}>{flowLabel(job.flow)}</Badge>
          ) : (
            <span className="faint">-</span>
          );
        },
        meta: { width: 130 },
      }),
      col.accessor("trigger", {
        header: "Trigger",
        cell: ({ row }) => (
          <span className="faint mono" style={{ fontSize: 11 }}>
            {row.original.trigger}
          </span>
        ),
        meta: { width: 110, filterSelect: true, filterLabel: "trigger" },
      }),
      col.accessor("status", {
        header: "Status",
        cell: ({ row }) => {
          const r = row.original;
          return (
            <Badge variant={TONE_VARIANT[statusTone(r.status)]}>
              {isLive(r) && <Dot live />}
              {r.status}
            </Badge>
          );
        },
        meta: { width: 120 },
      }),
      col.accessor("notified_at", {
        header: "Slack",
        cell: ({ getValue }) => {
          const notifiedAt = getValue();
          return notifiedAt ? (
            <Badge variant="outline-success" title={`Sent ${fmtDate(notifiedAt)} ${fmtTime(notifiedAt)}`}>
              Sent
            </Badge>
          ) : (
            <span className="faint">Not sent</span>
          );
        },
        meta: { width: 110 },
      }),
      col.accessor((r) => r.started_at ?? r.created_at, {
        id: "started",
        header: "Started",
        cell: ({ row }) => {
          const t = row.original.started_at ?? row.original.created_at;
          return (
            <span className="inline-flex flex-col" style={{ lineHeight: 1.3 }}>
              <span className="faint" style={{ fontSize: 10 }}>{fmtDate(t)}</span>
              <span className="num" style={{ fontSize: 11.5 }}>{fmtTime(t)}</span>
            </span>
          );
        },
        meta: { width: 150 },
      }),
      col.accessor((r) => elapsedSeconds(r) ?? 0, {
        id: "duration",
        header: "Duration",
        cell: ({ row }) => {
          const r = row.original;
          const dur = fmtDuration(elapsedSeconds(r));
          return (
            <span
              className="num"
              style={{
                fontSize: 11.5,
                color: isCrit(r) ? "var(--crit)" : "var(--ink-2)",
              }}
            >
              {dur}
              {isLive(r) && dur !== "-" ? "..." : ""}
            </span>
          );
        },
        meta: { width: 110 },
      }),
      col.accessor((r) => r.summary?.rows_emitted ?? r.progress?.rows_emitted ?? 0, {
        id: "rows",
        header: "Rows",
        cell: ({ row }) => {
          const r = row.original;
          const rows = r.summary?.rows_emitted ?? r.progress?.rows_emitted;
          return (
            <span className="num" style={isCrit(r) ? { color: "var(--crit)" } : undefined}>
              {fmtRows(rows)}
            </span>
          );
        },
        meta: { align: "right", width: 100, cellClassName: "num" },
      }),
      col.display({
        id: "open",
        header: "",
        cell: ({ row }) => (
          <Tag asChild>
            <Link to={`/runs/${row.original.id}`} style={{ textDecoration: "none" }} aria-label="open run">
              <ArrowRight size={12} aria-hidden />
            </Link>
          </Tag>
        ),
        meta: { align: "right", width: 60 },
        enableSorting: false,
        enableHiding: false,
      }),
    ];
  }, [jobById]);

  const serverFilters = (
    <>
      <select
        aria-label="category"
        className={`dt-facet${category !== "all" ? " active" : ""}`}
        value={category}
        onChange={(e) => {
          const next = e.currentTarget.value as "all" | FlowCategory;
          setCategory(next);
          // A flow outside the new category would leave the table empty for no visible reason.
          if (next !== "all" && flow !== "all" && FLOW_CATEGORY[flow] !== next) setFlow("all");
        }}
      >
        <option value="all">all categories</option>
        {Object.values(FlowCategory).map((c) => (
          <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>
        ))}
      </select>
      <select
        aria-label="flow"
        className={`dt-facet${flow !== "all" ? " active" : ""}`}
        value={flow}
        onChange={(e) => setFlow(e.currentTarget.value as "all" | Flow)}
      >
        <option value="all">all flows</option>
        {FLOWS.filter((f) => category === "all" || FLOW_CATEGORY[f] === category).map((f) => (
          <option key={f} value={f}>{flowLabel(f)}</option>
        ))}
      </select>
      <label className="row text-[12px]" style={{ gap: 6 }}>
        <Switch checked={showNoop} onCheckedChange={setShowNoop} aria-label="show no-op runs" />
        no-op runs
      </label>
      <select
        className={`dt-facet${jobId !== "all" ? " active" : ""}`}
        value={jobId}
        onChange={(e) => setJobId(e.currentTarget.value)}
      >
        <option value="all">all jobs</option>
        {selectableJobs.map((j) => (
          <option key={j.id} value={j.id}>{j.name}</option>
        ))}
      </select>
      <Seg
        brand
        value={status}
        onChange={setStatus}
        options={STATUS_FILTERS.map((f) => ({ value: f, label: f }))}
      />
    </>
  );

  return (
    <>
      <PageHeader
        className="mb-3.5"
        title="Runs"
        description="All runs - newest first - lineage to ClickHouse rows"
      />

      {runsQuery.isError && (
        <Panel className="mb-3.5">
          <Panel.Body>
            <ErrorState
              compact
              title="Could not load runs"
              message="Is the control plane up?"
              onRetry={() => runsQuery.refetch()}
            />
          </Panel.Body>
        </Panel>
      )}

      <Panel>
        <Panel.Head title="Runs">
          {runningCount > 0 && (
            <Badge variant="outline-success" className="ml-1.5">
              <Dot live /> {runningCount} running
            </Badge>
          )}
          <div className="flex-1" />
          <span className="faint num text-[11px]">showing {runs.length}</span>
        </Panel.Head>
        <Panel.Body tight>
          <DataTable
            tableKey="runs"
            columns={columns}
            data={runs}
            getRowId={(r) => r.id}
            globalPlaceholder="Search run / job / trigger..."
            csvFilename="runs"
            toolbarExtras={serverFilters}
            pinFirstColumn
            fillHeight
            rowClassName={(r) =>
              r.status === "failed" || r.status === "timed_out" ? "row-crit" : undefined
            }
            isLoading={runsQuery.isLoading}
            emptyMessage="No runs match this filter."
          />
        </Panel.Body>
      </Panel>
    </>
  );
}
